/**
 * Super-admin → Upgrade Suggestions
 *
 * CRUD over `suggestion_rules`. Each rule evaluates `metric {op} threshold` against
 * a tenant's live usage; matching rules surface a "Recommended for you" banner on
 * the tenant's /app/billing/plans page.
 *
 * Every action (create/update/delete) is audit-logged via the backend.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, BookOpen, BarChart3, Loader2 } from "lucide-react";
import Modal, { GoldButton, GhostButton, PageHeader, Field, Input, Select } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const METRICS = [
    { value: "users_count", label: "Active users (count)" },
    { value: "cases_count", label: "Cases (count)" },
    { value: "ai_spend_30d", label: "AI spend last 30d (₹)" },
    { value: "ecourts_calls_30d", label: "eCourts API calls last 30d (count)" },
    { value: "storage_mb", label: "Storage used (MB)" },
];
const OPS = [
    { value: ">", label: "greater than" },
    { value: ">=", label: "greater than or equal" },
    { value: "<", label: "less than" },
    { value: "<=", label: "less than or equal" },
    { value: "==", label: "equal to" },
];

export default function AdminSuggestionRules() {
    const [rules, setRules] = useState([]);
    const [plans, setPlans] = useState([]);
    const [impressions, setImpressions] = useState([]);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [r, p, i] = await Promise.all([
                api.get("/admin/suggestion-rules"),
                api.get("/plans"),
                api.get("/admin/suggestion-impressions?limit=50").catch(() => ({ data: [] })),
            ]);
            setRules(r.data || []); setPlans(p.data || []); setImpressions(i.data || []);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const newRule = () => setEditing({
        metric: "users_count", op: ">", threshold: 10,
        suggest_plan_id: plans[0]?.id || "", reason_text: "", severity: "info", enabled: true,
    });

    const save = async () => {
        if (!editing.suggest_plan_id) { toast.error("Pick a target plan"); return; }
        setBusy(true);
        try {
            if (editing.id) {
                const { id, ...patch } = editing;
                await api.patch(`/admin/suggestion-rules/${id}`, patch);
                toast.success("Rule updated");
            } else {
                await api.post("/admin/suggestion-rules", editing);
                toast.success("Rule created");
            }
            setEditing(null); load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this rule?")) return;
        try { await api.delete(`/admin/suggestion-rules/${id}`); toast.success("Rule deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const toggle = async (rule) => {
        try { await api.patch(`/admin/suggestion-rules/${rule.id}`, { enabled: !rule.enabled }); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const planMap = Object.fromEntries(plans.map(p => [p.id, p]));
    const rulesCtrl = useTableControls(rules, {
        accessors: {
            enabled: (r) => r.enabled ? "on" : "off",
            suggest_plan: (r) => planMap[r.suggest_plan_id]?.name || "",
        },
    });

    return (
        <>
            <PageHeader title="Upgrade Suggestions" subtitle="Rule-based plan recommendations · every match logged for funnel analytics" action={
                <GoldButton onClick={newRule} data-testid="new-rule"><Plus className="w-4 h-4 inline mr-1"/>New rule</GoldButton>
            }/>

            <ExplainerCard plansCount={plans.length}/>

            <div className="border border-border overflow-x-auto mb-8" data-testid="rules-table">
                <FilterChip ctrl={rulesCtrl} className="m-2"/>
                <table className="w-full text-sm">
                    <thead className="bg-muted">
                        <tr>
                            <SortableTh field="enabled" ctrl={rulesCtrl} className="text-left px-3 py-2">Enabled</SortableTh>
                            <SortableTh field="metric" ctrl={rulesCtrl} className="text-left px-3 py-2">Metric</SortableTh>
                            <SortableTh field="op" ctrl={rulesCtrl} className="text-left px-3 py-2">Op</SortableTh>
                            <SortableTh field="threshold" ctrl={rulesCtrl} className="text-left px-3 py-2">Threshold</SortableTh>
                            <SortableTh field="suggest_plan" ctrl={rulesCtrl} className="text-left px-3 py-2">Suggest plan</SortableTh>
                            <SortableTh field="reason_text" ctrl={rulesCtrl} className="text-left px-3 py-2">Reason</SortableTh>
                            <SortableTh field="severity" ctrl={rulesCtrl} className="text-left px-3 py-2">Severity</SortableTh>
                            <SortableTh field="created_at" ctrl={rulesCtrl} className="text-left px-3 py-2">Created</SortableTh>
                            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider font-medium"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rulesCtrl.rows.length === 0 && (
                            <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground text-xs">
                                No rules yet. Click <strong>New rule</strong> to define one. Tenants on /app/billing/plans will see matched rules as a gold banner.
                            </td></tr>
                        )}
                        {rulesCtrl.rows.map(r => (
                            <tr key={r.id} className="border-t border-border" data-testid={`rule-${r.id}`}>
                                <td className="px-3 py-2">
                                    <button onClick={() => toggle(r)} data-testid={`toggle-${r.id}`}
                                        className={`text-[10px] px-2 py-1 uppercase tracking-[0.1em] ${r.enabled ? "bg-emerald-950/40 text-emerald-300" : "bg-border text-muted-foreground"}`}>
                                        {r.enabled ? "On" : "Off"}
                                    </button>
                                </td>
                                <td className="px-3 py-2 font-mono text-xs">{r.metric}</td>
                                <td className="px-3 py-2 font-mono text-xs">{r.op}</td>
                                <td className="px-3 py-2 font-mono text-xs">{r.threshold}</td>
                                <td className="px-3 py-2">{planMap[r.suggest_plan_id]?.name || <span className="text-rose-400">missing plan</span>}</td>
                                <td className="px-3 py-2 text-xs text-muted-foreground max-w-xs truncate">{r.reason_text || <span className="text-muted-foreground">—</span>}</td>
                                <td className="px-3 py-2">
                                    <span className={`text-[10px] px-2 py-1 uppercase tracking-[0.1em] ${r.severity === "warn" ? "bg-amber-950/40 text-amber-300" : "bg-blue-950/40 text-blue-300"}`}>{r.severity}</span>
                                </td>
                                <td className="px-3 py-2 text-[10px] text-muted-foreground">
                                    {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditing(r)} data-testid={`edit-${r.id}`}
                                            className="text-[10px] uppercase tracking-[0.1em] text-primary hover:text-amber-300">Edit</button>
                                        <button onClick={() => del(r.id)} data-testid={`delete-${r.id}`}
                                            className="text-[10px] uppercase tracking-[0.1em] text-rose-400 hover:text-rose-300">
                                            <Trash2 className="w-3 h-3 inline"/>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ImpressionsBlock items={impressions} planMap={planMap}/>

            {editing && (
                <Modal open={true} onClose={() => setEditing(null)} title={editing.id ? "Edit rule" : "New suggestion rule"} size="lg" testId="rule-modal">
                    <div className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Metric">
                                <Select options={METRICS} value={editing.metric} onChange={e => setEditing({...editing, metric: e.target.value})} name="metric"/>
                            </Field>
                            <Field label="Operator">
                                <Select options={OPS} value={editing.op} onChange={e => setEditing({...editing, op: e.target.value})} name="op"/>
                            </Field>
                            <Field label="Threshold">
                                <Input type="number" value={editing.threshold} onChange={e => setEditing({...editing, threshold: Number(e.target.value)})} data-testid="threshold-input"/>
                            </Field>
                            <Field label="Severity">
                                <Select options={[{value:"info",label:"Info (blue)"},{value:"warn",label:"Warn (amber)"}]} value={editing.severity} onChange={e => setEditing({...editing, severity: e.target.value})} name="severity"/>
                            </Field>
                        </div>
                        <Field label="Suggest plan" hint="Which plan to recommend when this rule matches">
                            <Select options={plans.map(p => ({value: p.id, label: `${p.name} · ₹${Number(p.price||0).toLocaleString("en-IN")}/${p.billing_cycle || p.cycle}`}))}
                                value={editing.suggest_plan_id} onChange={e => setEditing({...editing, suggest_plan_id: e.target.value})} name="plan"/>
                        </Field>
                        <Field label="Reason text shown to tenant" hint="e.g. ‘Your team has grown past 10 users — Pro removes the seat cap.’">
                            <Input value={editing.reason_text} onChange={e => setEditing({...editing, reason_text: e.target.value})} data-testid="reason-input"/>
                        </Field>

                        <PreviewBlock rule={editing} plans={plans}/>

                        <div className="flex items-center gap-2 pt-2">
                            <input type="checkbox" id="enabled-chk" checked={editing.enabled} onChange={e => setEditing({...editing, enabled: e.target.checked})}/>
                            <label htmlFor="enabled-chk" className="text-xs">Enabled — rule is evaluated on every tenant page load</label>
                        </div>

                        <div className="flex justify-end gap-2 pt-3 border-t border-border">
                            <GhostButton onClick={() => setEditing(null)}>Cancel</GhostButton>
                            <GoldButton onClick={save} disabled={busy} data-testid="save-rule">{busy ? "Saving…" : "Save rule"}</GoldButton>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

function ExplainerCard({ plansCount }) {
    return (
        <div className="border border-primary/30 bg-primary/5 p-5 mb-6" data-testid="explainer">
            <div className="flex items-center gap-2 mb-3"><BookOpen className="w-4 h-4 text-primary"/><div className="text-sm font-medium">How suggestion rules work</div></div>
            <div className="grid md:grid-cols-2 gap-4 text-xs text-muted-foreground">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Evaluation</div>
                    <p>Every time a tenant loads <span className="font-mono text-foreground">/app/billing/plans</span>, the backend computes the tenant's live metrics and evaluates each <strong>enabled</strong> rule. Matching rules surface as a gold "Recommended for you" banner with the rule's reason text.</p>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Example</div>
                    <pre className="font-mono text-[11px] bg-background border border-border p-2 overflow-x-auto whitespace-pre-wrap">{`{
  "metric": "users_count",
  "op": ">",
  "threshold": 10,
  "suggest_plan_id": "<Pro plan id>",
  "reason_text": "Team grew past 10 users — Pro removes the seat cap.",
  "severity": "warn"
}`}</pre>
                </div>
            </div>
            <div className="text-[10px] text-muted-foreground mt-3">{plansCount} plan{plansCount === 1 ? "" : "s"} available to recommend · all rule changes audit-logged under <span className="font-mono">suggestion_rule.*</span></div>
        </div>
    );
}

function PreviewBlock({ rule, plans }) {
    const plan = plans.find(p => p.id === rule.suggest_plan_id);
    const metricLabel = METRICS.find(m => m.value === rule.metric)?.label || rule.metric;
    return (
        <div className="border border-border bg-background p-3 text-xs">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Live preview · this is what a matching tenant sees</div>
            <div className="border border-primary/40 bg-primary/5 p-3 flex items-start gap-2">
                <Sparkles className="w-3 h-3 text-primary mt-0.5 shrink-0"/>
                <div>
                    <div className="text-primary text-[11px]">Recommended for you</div>
                    <div className="text-muted-foreground">→ <strong className="text-foreground">{plan?.name || "<pick a plan>"}</strong> — {rule.reason_text || `${metricLabel} ${rule.op} ${rule.threshold}`}</div>
                </div>
            </div>
        </div>
    );
}

function ImpressionsBlock({ items, planMap }) {
    const ctrl = useTableControls(items, {
        accessors: {
            when: (i) => i.created_at,
            tenant: (i) => i.tenant_id || "",
            suggestions: (i) => (i.suggestions || []).map(s => s.plan_name || planMap[s.plan_id]?.name || s.plan_id).join(" "),
        },
    });
    return (
        <div className="border border-border" data-testid="impressions-block">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-primary"/>
                <div className="text-sm font-medium">Recent impressions ({items.length})</div>
                <div className="text-[10px] text-muted-foreground ml-auto">Logged whenever a tenant saw a "Recommended for you" suggestion</div>
            </div>
            {items.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground text-xs">No impressions logged yet. Rules need to be enabled and tenants need to load the page.</div>
            ) : (
                <>
                <FilterChip ctrl={ctrl} className="m-2"/>
                <table className="w-full text-xs">
                    <thead className="bg-muted">
                        <tr>
                            <SortableTh field="when" ctrl={ctrl} className="px-3 py-2 text-left">When</SortableTh>
                            <SortableTh field="tenant" ctrl={ctrl} className="px-3 py-2 text-left">Tenant</SortableTh>
                            <SortableTh field="suggestions" ctrl={ctrl} className="px-3 py-2 text-left">Suggestions</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {ctrl.rows.map(i => (
                            <tr key={i.id} className="border-t border-border">
                                <td className="px-3 py-2 text-muted-foreground">{new Date(i.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}</td>
                                <td className="px-3 py-2 font-mono text-[10px]">{i.tenant_id}</td>
                                <td className="px-3 py-2">
                                    {(i.suggestions || []).map((s, n) => (
                                        <div key={n} className="text-muted-foreground">
                                            <strong className="text-foreground">{s.plan_name || planMap[s.plan_id]?.name || s.plan_id}</strong>
                                            <span className="text-muted-foreground"> · {s.metric}={String(s.value)} {s.op} {s.threshold}</span>
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </>
            )}
        </div>
    );
}
